/** @author Rajkumar Rathod */
import { GLOBAL_NAME, REQUEST_APPOINTMENT_FLOW, SDK_VERSION } from './constants';
import { AnchoredMenu } from './menu';
import { readScriptConfig, resolveConfig, type ResolvedConfig } from './config';
import { Emitter } from './emitter';
import { Session } from './session';
import { TriggerBinder } from './triggers';
import { log, setDebug } from './utils';
import type {
  OpenOptions,
  PrefillData,
  WidgetAnyHandler,
  WidgetConfig,
  WidgetEventHandler,
  WidgetEventName,
  WidgetFlow,
} from './types';

export type * from './types';

/** Shape of the async stub a host page may define before the bundle arrives. */
interface QueueStub {
  q?: Array<[string, unknown[]]>;
}

/**
 * How long the "You're registered" confirmation stays up before Find a Doctor
 * replaces it. Long enough to read, short enough not to feel like a stall.
 */
const REGISTRATION_HANDOFF_MS = 900;

/**
 * Close reasons that end the patient's visit, as opposed to the internal
 * `close('api')` that `open()` fires when it swaps one flow for another.
 *
 * The distinction matters: the registration -> Find a Doctor handoff closes a
 * session on purpose, and treating that as "the patient left" would throw away
 * the id they just registered for.
 */
const DISMISSAL_REASONS = new Set(['user', 'backdrop', 'escape', 'embed', 'error']);

export class EhrWidgetClient {
  readonly version = SDK_VERSION;

  private cfg: ResolvedConfig | null = null;
  private readonly emitter = new Emitter();
  private binder: TriggerBinder | null = null;
  private session: Session | null = null;
  private menu: AnchoredMenu | null = null;

  /**
   * The uuid returned by patient registration, held for the rest of this
   * visit and sent in every subsequent flow's `host:init` prefill — that is
   * how the booking form gets a `patientId` to submit.
   *
   * Deliberately in memory rather than storage. `sessionStorage` on the EHR
   * origin is shared by every tenant's embed in the tab and outlives the
   * overlay, so a patient id left there could be read by the next clinic's
   * widget. This dies with the page, and is cleared sooner on close.
   */
  private patientId: string | null = null;

  private unbindEmbedEvents: (() => void) | null = null;
  private handoffTimer: number | null = null;

  /** Configure the SDK. Safe to call again to change config; triggers are rebound. */
  init(config: WidgetConfig): this {
    const resolved = resolveConfig(config);
    setDebug(resolved.debug);

    if (this.cfg) {
      log.debug('re-initialising with new config');
      this.binder?.unbind();
      this.binder = null;
    }
    this.cfg = resolved;

    if (config.onEvent) this.emitter.on('*', config.onEvent);

    this.bindEmbedEvents();

    if (resolved.autoBind) {
      this.binder = new TriggerBinder(resolved, (flow, prefill, trigger) => {
        if (flow === REQUEST_APPOINTMENT_FLOW) {
          this.openMenu(trigger);
          return;
        }
        this.open(flow, { prefill });
      });
      this.binder.bind();
    }

    log.debug(`initialised v${SDK_VERSION}`, {
      clientId: resolved.clientId,
      origin: resolved.origin,
      triggers: resolved.triggers,
    });
    return this;
  }

  /**
   * React to what an embed asks the host page to do: hold the patient id it
   * established — by registration or by sign-in — and switch flows on request.
   *
   * Both exist because every flow is its own iframe. Registration is the only
   * place the widget learns a patient id of its own and the flow that needs it
   * (booking) runs elsewhere; likewise "New Patient? Register" on the login
   * screen cannot swap panels in place, because the registration form is a
   * different page. The host outlives every frame, so it is the only thing that
   * can join them up.
   *
   * Re-bound on every `init()` because `destroy()` clears the emitter.
   */
  private bindEmbedEvents(): void {
    this.unbindEmbedEvents?.();

    const offNavigate = this.emitter.on('navigate', (payload) => {
      // `flow` is deliberately not part of the public `navigate` payload: a host
      // listening for "the embed wants me to go somewhere on my own site" gets
      // `path`/`url`, while this is a request only the SDK can serve.
      const flow = (payload as { flow?: unknown } | undefined)?.flow;
      if (typeof flow !== 'string' || !flow) return;
      this.openRequestedFlow(flow);
    });

    const offCreated = this.emitter.on('patient.created', (payload) => {
      const id = payload?.patientId;
      if (typeof id !== 'string' || !id) return;

      this.patientId = id;
      log.debug('holding patient id for this session', id);

      /*
       * Registration also happens *inside* Find a Doctor now, as the step in
       * front of the booking form for a patient who came straight to the doctor
       * list. Opening the flow there would reload its iframe and throw away the
       * doctor they picked and the form they were about to fill — so the id is
       * held, as always, and the handoff is skipped.
       */
      if (this.session?.flow === 'find-doctor') {
        log.debug('find-doctor is already open; keeping the patient where they are');
        return;
      }

      // Hand off to Find a Doctor, which is where a freshly registered patient
      // is going. Timed so the confirmation is readable rather than a flash.
      this.handoffTimer = window.setTimeout(() => {
        this.handoffTimer = null;
        this.open('find-doctor');
      }, REGISTRATION_HANDOFF_MS);
    });

    /*
     * A sign-in through the widget's own login screen. Held exactly like a
     * registration's id — it is the same fact, arrived at differently — and with
     * no handoff: the login screen asks for Find a Doctor itself, with
     * `navigate`, and only when it is the standalone flow.
     *
     * This is what lets the embed stop trusting its own storage. The id now
     * reaches the next iframe through the host, which forgets it the moment the
     * patient leaves, so nothing outlives the visit.
     */
    const offIdentified = this.emitter.on('patient.identified', (payload) => {
      const id = payload?.patientId;
      if (typeof id !== 'string' || !id) return;
      this.patientId = id;
      log.debug('holding signed-in patient id for this session', id);
    });

    const offClosed = this.emitter.on('widget.closed', (payload) => {
      if (DISMISSAL_REASONS.has(payload?.reason)) this.forgetPatient();
    });

    this.unbindEmbedEvents = () => {
      offNavigate();
      offCreated();
      offIdentified();
      offClosed();
    };
  }

  /**
   * Open a flow the embed asked for by name.
   *
   * The name arrives over postMessage, so it is checked the same way `open()`
   * checks a host's own argument, plus two cases that are refused outright:
   *
   *  - `request-appointment` is a pseudo-flow that opens the anchored menu and
   *    needs a trigger element to hang off. Routing it here would try to load
   *    `/embed/request-appointment`, which does not exist.
   *  - the flow already on screen, because an embed that emitted this on render
   *    rather than on click would otherwise reload its own iframe forever.
   */
  private openRequestedFlow(flow: string): void {
    if (!this.cfg) return;

    if (flow === REQUEST_APPOINTMENT_FLOW) {
      log.warn(`embed asked for "${flow}", which is a host-page menu, not an embed flow`);
      return;
    }
    if (!this.cfg.flows[flow] && !/^[a-z0-9-]+$/i.test(flow)) {
      log.error(`embed asked for unknown flow "${flow}"`);
      return;
    }
    if (this.session?.flow === flow) {
      log.debug(`embed asked for "${flow}", which is already open; ignoring`);
      return;
    }

    log.debug('embed requested flow', flow);
    this.open(flow);
  }

  /**
   * Drop the registered patient id and any pending handoff.
   *
   * Cancelling the timer matters as much as clearing the id: without it,
   * closing the widget during the confirmation would pop Find a Doctor open
   * again a moment later.
   */
  private forgetPatient(): void {
    if (this.handoffTimer !== null) {
      window.clearTimeout(this.handoffTimer);
      this.handoffTimer = null;
    }
    if (this.patientId) log.debug('clearing held patient id');
    this.patientId = null;
  }

  /** Open a flow programmatically. Any currently open widget is replaced. */
  open(flow: WidgetFlow, options: OpenOptions = {}): void {
    const cfg = this.cfg;
    if (!cfg) {
      log.error(`${GLOBAL_NAME}.open() called before init(). Call ${GLOBAL_NAME}.init({ clientId }) first.`);
      return;
    }
    if (!cfg.flows[flow] && !/^[a-z0-9-]+$/i.test(String(flow))) {
      log.error(`unknown flow "${flow}"`);
      return;
    }
    if (this.session) this.session.close('api');

    /*
     * A registered patient's id rides along on every later flow. It goes in
     * *before* the caller's own prefill so an explicit `open(..., { prefill })`
     * can still override it — the caller knows something we do not.
     */
    const opts: OpenOptions = this.patientId
      ? { ...options, prefill: { patientId: this.patientId, ...options.prefill } }
      : options;

    const session = new Session(cfg, flow, opts, this.emitter, () => {
      if (this.session === session) this.session = null;
    });
    this.session = session;
    session.start();
  }

  /**
   * Show the New / Existing patient menu under `trigger`.
   *
   * Clicking the same trigger again closes it, which is what a menu button is
   * expected to do — otherwise a second click would silently rebuild it in
   * place and look like nothing happened.
   *
   * That toggle reads `this.menu`, so the handle has to be dropped the moment
   * the menu goes — including when the menu decided that for itself. It did not
   * used to be, and an outside click left the state saying "open" while the
   * page said otherwise: the next click was spent closing a menu that was
   * already gone, and it took two to reopen (ECRH-5987).
   */
  openMenu(trigger: HTMLElement): void {
    if (!this.cfg) {
      log.error(`${GLOBAL_NAME}.openMenu() called before init().`);
      return;
    }

    const wasOpen = this.menu !== null;
    this.closeMenu();
    if (wasOpen) return;

    const menu = new AnchoredMenu(
      trigger,
      [
        { label: 'New Patient', onSelect: () => this.open('register') },
        { label: 'Existing Patient', onSelect: () => this.open('login') },
      ],
      // Guarded on identity rather than assigning `null` outright, so a menu
      // closing *because* another one opened cannot clear the new one's handle.
      () => {
        if (this.menu === menu) this.menu = null;
      }
    );
    this.menu = menu;
    menu.open();
  }

  closeMenu(): void {
    this.menu?.close();
    this.menu = null;
  }

  close(): void {
    this.closeMenu();
    // Closing the widget ends the visit, so the registered patient id goes with
    // it. `open()`'s internal `close('api')` does not come through here, which
    // is what keeps the registration -> Find a Doctor handoff intact.
    this.forgetPatient();
    this.session?.close('api');
  }

  isOpen(): boolean {
    return this.session !== null;
  }

  /** True once `init()` has run successfully. */
  isInitialized(): boolean {
    return this.cfg !== null;
  }

  /** Merge values into the default prefill used by subsequent opens. */
  setPrefill(prefill: PrefillData): void {
    if (!this.cfg) return;
    this.cfg.prefill = { ...this.cfg.prefill, ...prefill };
  }

  on<K extends WidgetEventName>(name: K, handler: WidgetEventHandler<K>): () => void;
  on(name: '*', handler: WidgetAnyHandler): () => void;
  on(name: string, handler: (arg: never) => void): () => void {
    return this.emitter.on(name as '*', handler as WidgetAnyHandler);
  }

  off<K extends WidgetEventName>(name: K | '*', handler: WidgetEventHandler<K> | WidgetAnyHandler): void {
    this.emitter.off(name, handler);
  }

  once<K extends WidgetEventName>(name: K, handler: WidgetEventHandler<K>): () => void {
    return this.emitter.once(name, handler);
  }

  /** Tear everything down: close the widget, unbind triggers, drop all handlers. */
  destroy(): void {
    this.closeMenu();
    this.forgetPatient();
    this.unbindEmbedEvents?.();
    this.unbindEmbedEvents = null;
    this.session?.close('api');
    this.binder?.unbind();
    this.binder = null;
    this.emitter.clear();
    this.cfg = null;
  }

  /** @internal Replay calls captured by the async stub before this bundle loaded. */
  drain(queue: Array<[string, unknown[]]>): void {
    for (const entry of queue) {
      const method = entry[0];
      const args = Array.from(entry[1] ?? []);
      const fn = (this as unknown as Record<string, unknown>)[method];
      if (typeof fn === 'function') {
        try {
          (fn as (...a: unknown[]) => unknown).apply(this, args);
        } catch (err) {
          log.error(`queued ${GLOBAL_NAME}.${method}() failed:`, err);
        }
      } else {
        log.warn(`queued call to unknown method ${GLOBAL_NAME}.${method}()`);
      }
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                   */
/* -------------------------------------------------------------------------- */

type GlobalScope = typeof globalThis & Record<string, unknown>;
const scope = globalThis as GlobalScope;

function bootstrap(): EhrWidgetClient {
  const existing: unknown = scope[GLOBAL_NAME];

  if (existing instanceof EhrWidgetClient) {
    // Two copies of the bundle on one page (common when a tag manager and a hardcoded
    // <script> both load it). Keep the first so handlers and open sessions survive.
    log.warn(
      `${GLOBAL_NAME} is already loaded (v${existing.version}); ignoring duplicate v${SDK_VERSION}.`
    );
    return existing;
  }

  const instance = new EhrWidgetClient();
  scope[GLOBAL_NAME] = instance;

  const queued = (existing as QueueStub | undefined)?.q;
  if (Array.isArray(queued) && queued.length) {
    log.debug(`draining ${queued.length} queued call(s)`);
    instance.drain(queued);
  }

  // Auto-init from <script data-client-id="…">, unless a queued call already init'd us.
  try {
    const fromTag = readScriptConfig();
    if (fromTag.autoInit && fromTag.clientId && !instance.isInitialized()) {
      instance.init(fromTag as WidgetConfig);
    }
  } catch (err) {
    log.error('auto-init from script attributes failed:', err);
  }

  // Lets host pages that load the bundle async know when the API is usable.
  try {
    window.dispatchEvent(new CustomEvent('ehr-widget:loaded', { detail: { version: SDK_VERSION } }));
  } catch {
    /* CustomEvent unavailable — nothing depends on this. */
  }

  return instance;
}

export const EhrWidget = bootstrap();
export default EhrWidget;
